import React, { useEffect, useState } from "react";
import { getAdminJobDebug, type AdminJobDebug } from "../api.js";
import { usd } from "./format.js";

/** Under this, an uploaded recording is almost certainly empty or cut short. */
export const SUSPICIOUS_RECORDING_BYTES = 10 * 1024;

/** "12.4 MB", "830 KB", "0 bytes". */
export function bytes(size: number): string {
  if (size < 1024) return `${size} bytes`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const seconds = (ms: number | null) => (ms === null ? null : `${(ms / 1000).toFixed(1)} s`);
const clock = (at: string) => at.slice(11, 19);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-1.5">
    <h4 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-400">{title}</h4>
    {children}
  </section>
);

const Pre: React.FC<{ children: string }> = ({ children }) => (
  <pre className="max-h-[280px] overflow-auto whitespace-pre-wrap break-words rounded-[6px] bg-bay-900 p-2 font-mono text-[11px] leading-relaxed text-ink-200">
    {children}
  </pre>
);

const json = (value: unknown) => JSON.stringify(value, null, 2);
const hasContent = (value: unknown) =>
  value !== null && value !== undefined && !(typeof value === "object" && Object.keys(value as object).length === 0);

/** What was recorded about one failed job. Pure, so a test can render it. */
export const JobDebugView: React.FC<{ debug: AdminJobDebug }> = ({ debug }) => {
  const { recording, failures, steps, calls } = debug;
  return (
    <div className="flex flex-col gap-3 border-t border-[var(--edge)] pt-2.5 text-[12px] text-ink-200">
      <Section title="Recording">
        {recording ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <a href={`/assets/${recording.key}?download`} className="text-scope-400 hover:underline">
              Download the recording
            </a>
            <span className="tabular-nums text-ink-400">
              {recording.filename} · {bytes(recording.sizeBytes)} · {recording.mimeType}
              {recording.durationSeconds !== null ? ` · ${recording.durationSeconds.toFixed(1)} s` : ""}
            </span>
            {recording.sizeBytes < SUSPICIOUS_RECORDING_BYTES ? (
              <span className="font-semibold text-[#b45309]">
                {recording.sizeBytes === 0 ? "The file is empty." : "The file is suspiciously small."}
              </span>
            ) : null}
          </div>
        ) : (
          <p className="text-ink-400">This project has no uploaded recording.</p>
        )}
      </Section>

      <Section title={`Failed tries (${failures.length})`}>
        {failures.length === 0 ? (
          <p className="text-ink-400">
            None recorded. Failures before tries were recorded show only the message above.
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {failures.map((failure, index) => (
              <li key={`${failure.at}:${index}`} className="flex flex-col gap-1">
                <span className="tabular-nums text-ink-400">
                  Try {failure.attempt} · {clock(failure.at)} UTC
                  {failure.step ? ` · step ${failure.step}` : ""}
                  {failure.code ? ` · ${failure.code}` : ""}
                  {failure.retryable ? " · would retry" : " · not retryable"}
                </span>
                <span className="break-words text-ink-100">{failure.message}</span>
                {failure.stack ? (
                  <details>
                    <summary className="cursor-pointer text-ink-400">Stack trace</summary>
                    <Pre>{failure.stack}</Pre>
                  </details>
                ) : null}
                {hasContent(failure.details) ? (
                  <details>
                    <summary className="cursor-pointer text-ink-400">Details</summary>
                    <Pre>{json(failure.details)}</Pre>
                  </details>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title={`Steps (${steps.length})`}>
        {steps.length === 0 ? (
          <p className="text-ink-400">No steps were recorded.</p>
        ) : (
          <ol className="flex flex-col gap-1">
            {steps.map((run, index) => (
              <li key={`${run.startedAt}:${index}`} className="flex flex-col gap-0.5">
                <span className="tabular-nums">
                  <span className="text-ink-400">
                    Try {run.attempt} · {clock(run.startedAt)}
                  </span>{" "}
                  <span className="font-mono text-ink-100">{run.step}</span>{" "}
                  <span className={run.status === "failed" ? "font-semibold text-[#b91c1c]" : "text-ink-300"}>
                    {run.status}
                  </span>
                  {seconds(run.durationMs) ? <span className="text-ink-400"> · {seconds(run.durationMs)}</span> : null}
                </span>
                {run.error ? <span className="break-words text-[#b91c1c]">{run.error}</span> : null}
                {hasContent(run.summary) ? (
                  <details>
                    <summary className="cursor-pointer text-ink-400">Summary</summary>
                    <Pre>{json(run.summary)}</Pre>
                  </details>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title={`Calls to outside services (${calls.length})`}>
        {calls.length === 0 ? (
          <p className="text-ink-400">None.</p>
        ) : (
          <ol className="flex flex-col gap-0.5 tabular-nums">
            {calls.map((call, index) => (
              <li key={`${call.at}:${index}`}>
                <span className="text-ink-400">{clock(call.at)}</span> {call.provider}
                {call.operation ? ` · ${call.operation}` : ` · ${call.kind}`}
                {call.model ? ` · ${call.model}` : ""}
                {call.inputTokens !== null && call.outputTokens !== null
                  ? ` · ${call.inputTokens.toLocaleString("en-US")} in / ${call.outputTokens.toLocaleString("en-US")} out`
                  : ` · ${call.quantity} ${call.unit}`}
                {` · ${usd(call.costUsd)}`}
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
};

/** Loads a failed job's record when its line is opened, with a button to copy all of it. */
export const JobDebugPanel: React.FC<{ jobId: string }> = ({ jobId }) => {
  const [debug, setDebug] = useState<AdminJobDebug | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);

  useEffect(() => {
    let live = true;
    setDebug(null);
    setError(null);
    getAdminJobDebug(jobId)
      .then((answer) => live && setDebug(answer))
      .catch((caught: unknown) => live && setError(caught instanceof Error ? caught.message : "Could not load"));
    return () => {
      live = false;
    };
  }, [jobId]);

  if (error) return <p className="text-[12px] text-[#b91c1c]">Couldn't load the debug record: {error}</p>;
  if (!debug) return <p className="text-[12px] text-ink-400">Loading the debug record…</p>;

  const copy = () => {
    navigator.clipboard
      .writeText(json(debug))
      .then(() => setCopied("yes"))
      .catch(() => setCopied("failed"));
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
          Copy debug report
        </button>
        {copied === "yes" ? <span className="text-[11.5px] text-ink-400">Copied</span> : null}
        {copied === "failed" ? <span className="text-[11.5px] text-[#b91c1c]">Couldn't copy</span> : null}
      </div>
      <JobDebugView debug={debug} />
    </>
  );
};
